import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../axios';
import { useToast } from '../components/ToastProvider';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../components/StatusBadge';
import MobileDataCard from '../components/MobileDataCard';

interface Company {
  id: string;
  name: string;
  domain?: string;
  status: 'AVAILABLE' | 'PENDING_CONTACT' | 'IN_NEGOTIATION' | 'DEAL_CLOSED';
  autoRejectNewLeads: boolean;
}

interface CompanyFormState {
  name: string;
  domain: string;
  status: Company['status'];
  autoRejectNewLeads: boolean;
}

const EMPTY_FORM: CompanyFormState = {
  name: '',
  domain: '',
  status: 'AVAILABLE',
  autoRejectNewLeads: false,
};

const CompanyManagement: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [formState, setFormState] = useState<CompanyFormState>(EMPTY_FORM);
  const formRef = useRef<HTMLFormElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const fetchCompanies = async () => {
    const response = await axios.get('/api/admin/companies');
    setCompanies(response.data || []);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const filteredCompanies = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return companies;
    }

    return companies.filter((company) =>
      [
        company.name,
        company.domain || '',
        t(`status.company.${company.status}`),
        company.autoRejectNewLeads ? t('companies.autoReject') : t('companies.accepting'),
      ].some((value) => value.toLowerCase().includes(normalizedQuery))
    );
  }, [companies, query, t]);

  const startCreate = () => {
    setEditingCompanyId(null);
    setFormState(EMPTY_FORM);
  };

  const startEdit = (company: Company) => {
    setEditingCompanyId(company.id);
    setFormState({
      name: company.name,
      domain: company.domain || '',
      status: company.status,
      autoRejectNewLeads: company.autoRejectNewLeads,
    });
    window.requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      nameInputRef.current?.focus();
    });
  };

  const saveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setBusy('save-company');
      if (editingCompanyId) {
        await axios.patch(`/api/admin/companies/${editingCompanyId}`, formState);
        toast.show(t('companyManagement.updated') as string, 'success');
      } else {
        await axios.post('/api/admin/companies', formState);
        toast.show(t('companyManagement.created') as string, 'success');
      }
      startCreate();
      fetchCompanies();
    } catch (error: any) {
      toast.show(String(error?.response?.data || t('companyManagement.saveError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const updateLeadPolicy = async (id: string, autoRejectNewLeads: boolean) => {
    try {
      setBusy(`${id}:policy:${autoRejectNewLeads}`);
      await axios.patch(`/api/admin/companies/${id}/lead-policy`, { autoRejectNewLeads });
      toast.show(t('companyManagement.policyUpdated') as string, 'success');
      fetchCompanies();
    } catch (error: any) {
      toast.show(String(error?.response?.data || t('companyManagement.policyError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const deleteCompany = async (id: string) => {
    if (!window.confirm(t('companyManagement.deleteConfirm') as string)) {
      return;
    }
    try {
      setBusy(`delete:${id}`);
      await axios.delete(`/api/admin/companies/${id}`);
      toast.show(t('companyManagement.deleted') as string, 'success');
      if (editingCompanyId === id) {
        startCreate();
      }
      fetchCompanies();
    } catch (error: any) {
      toast.show(String(error?.response?.data || t('companyManagement.deleteError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('companyManagement.title')}</h1>

      <form ref={formRef} onSubmit={saveCompany} className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">
              {t(editingCompanyId ? 'companyManagement.editTitle' : 'companyManagement.createTitle')}
            </div>
            <div className="text-xs text-gray-600">{t('companyManagement.subtitle')}</div>
          </div>
          <button type="button" onClick={startCreate} className="px-4 py-2 bg-white text-[#111D4E] border border-slate-200 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50">
            {t('companyManagement.new')}
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.company')}</label>
            <input ref={nameInputRef} value={formState.name} onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.domain')}</label>
            <input value={formState.domain} onChange={(e) => setFormState((prev) => ({ ...prev, domain: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('companies.status')}</label>
            <select value={formState.status} onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value as Company['status'] }))} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white">
              {['AVAILABLE', 'PENDING_CONTACT', 'IN_NEGOTIATION', 'DEAL_CLOSED'].map((status) => (
                <option key={status} value={status}>{t(`status.company.${status}`)}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-3 pt-7">
            <input type="checkbox" checked={formState.autoRejectNewLeads} onChange={(e) => setFormState((prev) => ({ ...prev, autoRejectNewLeads: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-[#47ABDB]" />
            <span className="text-sm">{t('companies.autoReject')}</span>
          </label>
        </div>
        <div className="px-5 pb-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={startCreate} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-semibold">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={busy === 'save-company'} className="px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md font-semibold hover:bg-[#3d8fb8]">
            {busy === 'save-company' ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('companyManagement.listTitle')}</div>
            <div className="text-xs text-gray-600">{t('companyManagement.listSubtitle')}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('companies.searchPlaceholder')}
              className="w-full sm:w-auto sm:min-w-56 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
            />
            <button type="button" onClick={fetchCompanies} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">
              {t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="space-y-3 p-4 md:hidden">
          {filteredCompanies.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">
              {query.trim() ? t('companies.searchEmpty') : t('companyManagement.empty')}
            </div>
          ) : (
            filteredCompanies.map((company) => (
              <MobileDataCard
                key={company.id}
                title={company.name}
                badge={<StatusBadge kind="company" status={company.status} />}
                subtitle={company.domain || '-'}
                rows={[
                  {
                    label: t('companies.leadPolicy'),
                    value: (
                      <button
                        type="button"
                        onClick={() => updateLeadPolicy(company.id, !company.autoRejectNewLeads)}
                        disabled={busy === `${company.id}:policy:true` || busy === `${company.id}:policy:false`}
                        className={`px-3 py-2 rounded-md text-sm font-semibold ${company.autoRejectNewLeads ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
                      >
                        {company.autoRejectNewLeads ? t('companies.autoReject') : t('companies.accepting')}
                      </button>
                    ),
                  },
                ]}
                actions={
                  <>
                    <button type="button" onClick={() => startEdit(company)} className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-md text-sm font-semibold">
                      {t('admin.leads.edit')}
                    </button>
                    <button type="button" onClick={() => deleteCompany(company.id)} disabled={busy === `delete:${company.id}`} className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-md text-sm font-semibold disabled:opacity-60">
                      {busy === `delete:${company.id}` ? t('common.loading') : t('admin.leads.delete')}
                    </button>
                  </>
                }
              />
            ))
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-left table-auto text-[15px] md:text-base">
            <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
              <tr>
                <th className="px-5 py-3 font-semibold">{t('submitLead.company')}</th>
                <th className="px-5 py-3 font-semibold">{t('submitLead.domain')}</th>
                <th className="px-5 py-3 font-semibold">{t('companies.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('companies.leadPolicy')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.leads.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-base text-gray-500">
                    {query.trim() ? t('companies.searchEmpty') : t('companyManagement.empty')}
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company, idx) => (
                  <tr key={company.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                    <td className="px-5 py-4 font-medium text-[#111D4E]">{company.name}</td>
                    <td className="px-5 py-4 text-sm">{company.domain || '-'}</td>
                    <td className="px-5 py-4">
                      <StatusBadge kind="company" status={company.status} />
                    </td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        onClick={() => updateLeadPolicy(company.id, !company.autoRejectNewLeads)}
                        disabled={busy === `${company.id}:policy:true` || busy === `${company.id}:policy:false`}
                        className={`px-3 py-1 rounded text-xs font-semibold ${company.autoRejectNewLeads ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
                      >
                        {company.autoRejectNewLeads ? t('companies.autoReject') : t('companies.accepting')}
                      </button>
                    </td>
                    <td className="px-5 py-4 space-x-2">
                      <button type="button" onClick={() => startEdit(company)} className="px-3 py-1 bg-slate-200 text-slate-700 rounded text-sm">
                        {t('admin.leads.edit')}
                      </button>
                      <button type="button" onClick={() => deleteCompany(company.id)} disabled={busy === `delete:${company.id}`} className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm disabled:opacity-60">
                        {busy === `delete:${company.id}` ? t('common.loading') : t('admin.leads.delete')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default CompanyManagement;
